import { Controller } from "@hotwired/stimulus"
import SignaturePad from "signature_pad"

export default class extends Controller {
  static targets = [
    "respWrapper", "temasWrapper", "collabWrapper",
    "personRowTemplate", "temaRowTemplate",
    "respRowsField", "temasRowsField", "collabRowsField",
    "horaInput", "durationInput",
    "signatureCanvas", "signatureModal",
    "collaboratorModal", "areaModal",
    "newCollabMatricula", "newCollabTipo", "newCollabName", "newCollabCargo",
    "newAreaName"
  ]

  static values = {
    collaboratorsUrl: String,
    createCollaboratorUrl: String,
    areasUrl: String,
    createAreaUrl: String,
    customData: Object
  }

  connect() {
    this._collaborators = []
    this._areas = []
    this._signaturePad = null
    this._currentSigRow = null

    this._loadCollaborators()
    this._loadAreas()
    this._populateEditData()

    // Listen for serialize event from report-form controller
    this.element.closest("[data-report-form-target='ddsFields']")
      ?.addEventListener("dds:serialize", () => this.serializeData())

    // Close popovers when clicking outside
    this._boundClosePopovers = (e) => this._closePopoversOnOutsideClick(e)
    document.addEventListener("click", this._boundClosePopovers)
  }

  disconnect() {
    document.removeEventListener("click", this._boundClosePopovers)
    if (this._signaturePad) {
      this._signaturePad.off()
      this._signaturePad = null
    }
  }

  // ── Data Loading ────────────────────────────────────────────

  async _loadCollaborators() {
    if (!this.collaboratorsUrlValue) return
    try {
      const res = await fetch(this.collaboratorsUrlValue, { headers: { Accept: "application/json" } })
      if (res.ok) this._collaborators = await res.json()
    } catch (e) {
      console.error("Failed to load DDS collaborators:", e)
    }
  }

  async _loadAreas() {
    if (!this.areasUrlValue) return
    try {
      const res = await fetch(this.areasUrlValue, { headers: { Accept: "application/json" } })
      if (res.ok) this._areas = await res.json()
    } catch (e) {
      console.error("Failed to load DDS areas:", e)
    }
  }

  // ── Responsável Rows ────────────────────────────────────────

  addRespRow(e) {
    if (e) e.preventDefault()
    const tmpl = this.personRowTemplateTarget.content.cloneNode(true)
    this.respWrapperTarget.appendChild(tmpl)
    return this.respWrapperTarget.lastElementChild
  }

  removePersonRow(e) {
    e.preventDefault()
    e.target.closest(".dds-person-row")?.remove()
  }

  // ── Tema Rows ───────────────────────────────────────────────

  addTemaRow(e) {
    if (e) e.preventDefault()
    const tmpl = this.temaRowTemplateTarget.content.cloneNode(true)
    this.temasWrapperTarget.appendChild(tmpl)
    return this.temasWrapperTarget.lastElementChild
  }

  removeTemaRow(e) {
    e.preventDefault()
    e.target.closest(".dds-tema-row")?.remove()
  }

  // ── Colaborador Rows ────────────────────────────────────────

  addCollabRow(e) {
    if (e) e.preventDefault()
    const tmpl = this.personRowTemplateTarget.content.cloneNode(true)
    this.collabWrapperTarget.appendChild(tmpl)
    return this.collabWrapperTarget.lastElementChild
  }

  // ── Collaborator Popover Dropdown ───────────────────────────

  toggleCollabPopover(e) {
    e.stopPropagation()
    const row = e.target.closest(".dds-person-row")
    if (!row) return

    const popover = row.querySelector(".dds-collab-popover")
    if (!popover) return

    this._closeAllPopovers(popover)

    if (popover.classList.contains("hidden")) {
      this._openCollabPopover(row, popover)
    } else {
      popover.classList.add("hidden")
    }
  }

  _openCollabPopover(row, popover) {
    const searchInput = popover.querySelector(".dds-collab-search")
    if (searchInput) searchInput.value = ""
    this._renderCollabList(popover, "")
    popover.classList.remove("hidden")
    if (searchInput) setTimeout(() => searchInput.focus(), 50)
  }

  filterCollabPopover(e) {
    e.stopPropagation()
    const popover = e.target.closest(".dds-collab-popover")
    if (!popover) return
    const query = e.target.value.trim().toLowerCase()
    this._renderCollabList(popover, query)
  }

  _renderCollabList(popover, query) {
    const listEl = popover.querySelector(".dds-collab-list")
    if (!listEl) return

    const filtered = this._collaborators.filter(c => {
      if (!query) return true
      return `${c.matricula} ${c.name}`.toLowerCase().includes(query)
    }).slice(0, 30)

    if (filtered.length === 0) {
      listEl.innerHTML = `<div class="px-3 py-4 text-xs text-gray-400 text-center">Nenhum resultado</div>`
    } else {
      listEl.innerHTML = filtered.map(c =>
        `<div class="dds-collab-item flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
              data-matricula="${this._esc(c.matricula || '')}"
              data-tipo="${this._esc(c.tipo || '')}"
              data-name="${this._esc(c.name || '')}">
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium text-gray-900">${this._esc(c.matricula || '')}</div>
            <div class="text-xs text-gray-500">${this._esc(c.name || '')}</div>
          </div>
        </div>`
      ).join("")

      listEl.querySelectorAll(".dds-collab-item").forEach(item => {
        item.addEventListener("click", (ev) => {
          ev.stopPropagation()
          const row = popover.closest(".dds-person-row")
          this._selectCollaborator(row, {
            matricula: item.dataset.matricula,
            tipo: item.dataset.tipo,
            name: item.dataset.name
          })
          popover.classList.add("hidden")
        })
      })
    }
  }

  _selectCollaborator(row, collab) {
    if (!row) return
    const display = row.querySelector("[data-field='matricula-display']")
    const hidden = row.querySelector("[data-field='matricula']")
    const tipo = row.querySelector("[data-field='tipo']")
    const nome = row.querySelector("[data-field='nome']")

    if (display) display.value = collab.matricula
    if (hidden) hidden.value = collab.matricula
    if (tipo) tipo.value = collab.tipo || ""
    if (nome) nome.value = collab.name || ""
  }

  openCollaboratorFromPopover(e) {
    e.preventDefault()
    e.stopPropagation()
    this._popoverOriginRow = e.target.closest(".dds-person-row")
    this._closeAllPopovers()
    this.openCollaboratorModal(e)
  }

  // ── Area Popover Dropdown ───────────────────────────────────

  toggleAreaPopover(e) {
    e.stopPropagation()
    const row = e.target.closest(".dds-person-row")
    if (!row) return

    const popover = row.querySelector(".dds-area-popover")
    if (!popover) return

    this._closeAllPopovers(popover)

    if (popover.classList.contains("hidden")) {
      this._openAreaPopover(row, popover)
    } else {
      popover.classList.add("hidden")
    }
  }

  _openAreaPopover(row, popover) {
    const searchInput = popover.querySelector(".dds-area-search")
    if (searchInput) searchInput.value = ""
    this._renderAreaList(popover, "")
    popover.classList.remove("hidden")
    if (searchInput) setTimeout(() => searchInput.focus(), 50)
  }

  filterAreaPopover(e) {
    e.stopPropagation()
    const popover = e.target.closest(".dds-area-popover")
    if (!popover) return
    const query = e.target.value.trim().toLowerCase()
    this._renderAreaList(popover, query)
  }

  _renderAreaList(popover, query) {
    const listEl = popover.querySelector(".dds-area-list")
    if (!listEl) return

    const filtered = this._areas.filter(a => {
      if (!query) return true
      return a.name.toLowerCase().includes(query)
    }).slice(0, 30)

    if (filtered.length === 0) {
      listEl.innerHTML = `<div class="px-3 py-4 text-xs text-gray-400 text-center">Nenhum resultado</div>`
    } else {
      listEl.innerHTML = filtered.map(a =>
        `<div class="dds-area-item flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
              data-area-name="${this._esc(a.name)}">
          <span class="text-sm text-gray-900">${this._esc(a.name)}</span>
        </div>`
      ).join("")

      listEl.querySelectorAll(".dds-area-item").forEach(item => {
        item.addEventListener("click", (ev) => {
          ev.stopPropagation()
          const row = popover.closest(".dds-person-row")
          this._selectArea(row, item.dataset.areaName)
          popover.classList.add("hidden")
        })
      })
    }
  }

  _selectArea(row, areaName) {
    if (!row) return
    const display = row.querySelector("[data-field='area-display']")
    const hidden = row.querySelector("[data-field='area']")
    if (display) display.value = areaName
    if (hidden) hidden.value = areaName
  }

  openAreaFromPopover(e) {
    e.preventDefault()
    e.stopPropagation()
    this._areaPopoverOriginRow = e.target.closest(".dds-person-row")
    this._closeAllPopovers()
    this.openAreaModal(e)
  }

  // ── Popover management ──────────────────────────────────────

  _closeAllPopovers(except) {
    this.element.querySelectorAll(".dds-collab-popover, .dds-area-popover").forEach(p => {
      if (p !== except) p.classList.add("hidden")
    })
  }

  _closePopoversOnOutsideClick(e) {
    if (!e.target.closest(".dds-collab-popover") &&
        !e.target.closest(".dds-area-popover") &&
        !e.target.matches("[data-field='matricula-display']") &&
        !e.target.matches("[data-field='area-display']")) {
      this._closeAllPopovers()
    }
  }

  // ── Signature Pad ───────────────────────────────────────────

  openSignaturePad(e) {
    e.preventDefault()
    this._currentSigRow = e.target.closest(".dds-person-row")
    this.signatureModalTarget.classList.remove("hidden")

    requestAnimationFrame(() => {
      const canvas = this.signatureCanvasTarget
      canvas.width = canvas.offsetWidth
      canvas.height = canvas.offsetHeight
      if (this._signaturePad) {
        this._signaturePad.clear()
      } else {
        this._signaturePad = new SignaturePad(canvas, {
          backgroundColor: "rgb(255, 255, 255)"
        })
      }
    })
  }

  clearSignature(e) {
    e.preventDefault()
    if (this._signaturePad) this._signaturePad.clear()
  }

  saveSignature(e) {
    e.preventDefault()
    if (!this._signaturePad || this._signaturePad.isEmpty()) return

    const dataUrl = this._signaturePad.toDataURL()
    if (this._currentSigRow) {
      const preview = this._currentSigRow.querySelector("[data-field='sig-preview']")
      const button = this._currentSigRow.querySelector("[data-field='sig-button']")

      if (preview) {
        preview.src = dataUrl
        preview.classList.remove("hidden")
      }
      if (button) button.classList.add("hidden")
      this._currentSigRow.dataset.signatureData = dataUrl
    }

    this.closeSignatureModal()
  }

  closeSignatureModal() {
    this.signatureModalTarget.classList.add("hidden")
    this._currentSigRow = null
  }

  // ── Add Collaborator Modal ──────────────────────────────────

  openCollaboratorModal(e) {
    e.preventDefault()
    this.newCollabMatriculaTarget.value = ""
    this.newCollabTipoTarget.value = ""
    this.newCollabNameTarget.value = ""
    this.newCollabCargoTarget.value = ""
    this.collaboratorModalTarget.classList.remove("hidden")
    this.newCollabMatriculaTarget.focus()
  }

  async saveCollaborator(e) {
    e.preventDefault()
    const matricula = this.newCollabMatriculaTarget.value.trim()
    const tipo = this.newCollabTipoTarget.value.trim()
    const name = this.newCollabNameTarget.value.trim()
    const cargo = this.newCollabCargoTarget.value.trim()

    if (!matricula || !name) return

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.createCollaboratorUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          Accept: "application/json"
        },
        body: JSON.stringify({ matricula, tipo, name, cargo })
      })
      if (res.ok) {
        const newCollab = { matricula, tipo, name }
        this._collaborators.push(newCollab)
        this.closeCollaboratorModal()

        if (this._popoverOriginRow) {
          this._selectCollaborator(this._popoverOriginRow, newCollab)
          this._popoverOriginRow = null
        }
      }
    } catch (err) {
      console.error("Failed to create collaborator:", err)
    }
  }

  closeCollaboratorModal() {
    this.collaboratorModalTarget.classList.add("hidden")
  }

  // ── Add Area Modal ──────────────────────────────────────────

  openAreaModal(e) {
    e.preventDefault()
    this.newAreaNameTarget.value = ""
    this.areaModalTarget.classList.remove("hidden")
    this.newAreaNameTarget.focus()
  }

  async saveArea(e) {
    e.preventDefault()
    const name = this.newAreaNameTarget.value.trim()
    if (!name) return

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.createAreaUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          Accept: "application/json"
        },
        body: JSON.stringify({ name })
      })
      if (res.ok) {
        this._areas.push({ name })
        this.closeAreaModal()

        if (this._areaPopoverOriginRow) {
          this._selectArea(this._areaPopoverOriginRow, name)
          this._areaPopoverOriginRow = null
        }
      }
    } catch (err) {
      console.error("Failed to create area:", err)
    }
  }

  closeAreaModal() {
    this.areaModalTarget.classList.add("hidden")
  }

  // ── Serialization ───────────────────────────────────────────

  serializeData() {
    // Serialize Responsável rows
    const respRows = []
    this.respWrapperTarget.querySelectorAll(".dds-person-row").forEach(row => {
      respRows.push({
        field1: row.querySelector("[data-field='matricula']")?.value || "",
        field2: row.querySelector("[data-field='tipo']")?.value || "",
        field3: row.querySelector("[data-field='nome']")?.value || "",
        field4: row.querySelector("[data-field='area']")?.value || "",
        field5: row.dataset.signatureData || ""
      })
    })

    // Serialize Tema rows
    const temasRows = []
    this.temasWrapperTarget.querySelectorAll(".dds-tema-row").forEach(row => {
      temasRows.push({
        field1: row.querySelector("[data-field='field1']")?.value || ""
      })
    })

    // Serialize Colaborador rows
    const collabRows = []
    this.collabWrapperTarget.querySelectorAll(".dds-person-row").forEach(row => {
      collabRows.push({
        field1: row.querySelector("[data-field='matricula']")?.value || "",
        field2: row.querySelector("[data-field='tipo']")?.value || "",
        field3: row.querySelector("[data-field='nome']")?.value || "",
        field4: row.querySelector("[data-field='area']")?.value || "",
        field5: row.dataset.signatureData || ""
      })
    })

    this.respRowsFieldTarget.value = JSON.stringify(respRows)
    this.temasRowsFieldTarget.value = JSON.stringify(temasRows)
    this.collabRowsFieldTarget.value = JSON.stringify(collabRows)
  }

  // ── Edit mode population ────────────────────────────────────

  _populateEditData() {
    const data = this.customDataValue
    if (!data || Object.keys(data).length === 0) {
      this.addRespRow()
      this.addTemaRow()
      this.addCollabRow()
      return
    }

    // Responsável rows
    if (data.dds_resp_rows && data.dds_resp_rows.length > 0) {
      data.dds_resp_rows.forEach(rowData => {
        const row = this.addRespRow()
        if (!row) return
        this._populatePersonRow(row, rowData)
      })
    } else {
      this.addRespRow()
    }

    // Tema rows
    if (data.dds_temas_rows && data.dds_temas_rows.length > 0) {
      data.dds_temas_rows.forEach(rowData => {
        const row = this.addTemaRow()
        if (!row) return
        const f1 = row.querySelector("[data-field='field1']")
        if (f1) f1.value = rowData.field1 || ""
      })
    } else {
      this.addTemaRow()
    }

    // Colaborador rows
    if (data.dds_collaborators_rows && data.dds_collaborators_rows.length > 0) {
      data.dds_collaborators_rows.forEach(rowData => {
        const row = this.addCollabRow()
        if (!row) return
        this._populatePersonRow(row, rowData)
      })
    } else {
      this.addCollabRow()
    }
  }

  _populatePersonRow(row, rowData) {
    const display = row.querySelector("[data-field='matricula-display']")
    const hidden = row.querySelector("[data-field='matricula']")
    const tipoInput = row.querySelector("[data-field='tipo']")
    const nomeInput = row.querySelector("[data-field='nome']")
    const areaDisplay = row.querySelector("[data-field='area-display']")
    const areaHidden = row.querySelector("[data-field='area']")
    const sigPreview = row.querySelector("[data-field='sig-preview']")
    const sigButton = row.querySelector("[data-field='sig-button']")

    if (display) display.value = rowData.field1 || ""
    if (hidden) hidden.value = rowData.field1 || ""
    if (tipoInput) tipoInput.value = rowData.field2 || ""
    if (nomeInput) nomeInput.value = rowData.field3 || ""
    if (areaDisplay) areaDisplay.value = rowData.field4 || ""
    if (areaHidden) areaHidden.value = rowData.field4 || ""

    if (rowData.field5 && sigPreview) {
      sigPreview.src = rowData.field5
      sigPreview.classList.remove("hidden")
      if (sigButton) sigButton.classList.add("hidden")
      row.dataset.signatureData = rowData.field5
    }
  }

  // ── Utilities ───────────────────────────────────────────────

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
